import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer, EntityList } from '@aimsos/ui';

import { formatTraitDate } from '../../../utils/traitDates.js';

import { BadgeCheck, FileText } from 'lucide-react';

/**
 * One fact of Truth (CT-5719), on `CartContainer` + `EntityList`'s slots:
 *
 *   label                         -> title
 *   Said / Documented / Observed  -> the badge beside the title (`pinned`)
 *   statement                     -> description
 *   "source · confirmed <date>"   -> secondaryMeta, behind a file icon
 *   Re-confirm due                -> a footer tag, beside the source line
 *   Request change                -> a ghost action, only where the API allows it
 *
 * The whole card opens the fact in the profile slide-out; the action does not.
 */
const TruthFactCard = memo(function TruthFactCard({ fact, busy = false, selected = false, onOpen, onRequestChange }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const handleOpen = useCallback(() => onOpen(fact), [onOpen, fact]);
  const handleRequestChange = useCallback(() => onRequestChange(fact), [onRequestChange, fact]);

  const view = useMemo(() => {
    const confirmed = formatTraitDate(fact.confirmedAt, language);
    const sourceLine = [fact.confirmedSource, confirmed ? t('knowledge.truth.card.confirmed', { date: confirmed }) : null]
      .filter(Boolean)
      .join(' · ');
    return {
      title: fact.label,
      icon: BadgeCheck,
      iconHighlight: { bgColor: 'Green', iconColor: 'Green' },
      pinned: fact.sourceKind ? { label: t(`knowledge.sourceTypes.${fact.sourceKind}`), variant: 'neutral' } : undefined,
      description: fact.value || undefined,
      secondaryMeta: sourceLine ? [{ icon: FileText, label: sourceLine }] : [],
      tags:
        fact.status === 'reconfirm_due'
          ? [{ label: <span data-testid="truth-fact-reconfirm">{t('knowledge.truth.status.reconfirm_due')}</span>, variant: 'warning' }]
          : undefined,
      primaryCta: fact.canRequestChange
        ? { label: t('knowledge.truth.actions.requestChange'), variant: 'ghost', onClick: handleRequestChange, disabled: busy }
        : undefined,
    };
  }, [fact, busy, language, handleRequestChange, t]);

  return (
    <div data-testid="truth-fact" data-fact-id={fact.id} data-status={fact.status}>
      <CartContainer
        size="S"
        variant="neutral"
        selected={selected}
        onClick={handleOpen}
        aria-label={t('knowledge.truth.card.open', { title: fact.label })}
      >
        <EntityList {...view} />
      </CartContainer>
    </div>
  );
});

export default TruthFactCard;
